/**
 * relayCommand
 *
 * A manual relay tap, followed from the tap to the point where the node itself
 * vouches for the result.
 *
 * WHY THE TAP NEEDED A STATE MACHINE AT ALL
 *   The tile used to flip to "ON" the instant the POST returned. That POST only
 *   queues the command on the server: the bridge has not collected it, the
 *   radio has not carried it, and the node has not closed anything. A queued
 *   command that the site later answers with
 *   "D2 on 0013A20041E38EE5 failed after 5 attempts: node returned an error
 *   status" — a real answer, from a real site — left the tile sitting on a
 *   confident blue ON with nothing anywhere saying otherwise.
 *
 * SO THERE ARE TWO SEPARATE CLAIMS, AND THEY GET DIFFERENT COLOURS
 *   - The site ACKNOWLEDGED the command (`CONFIRMED`/`OK` on the command row).
 *     That says the instruction arrived and was carried out, and nothing more.
 *     Amber.
 *   - The node REPORTED the output in the state we asked for, in a reading
 *     taken after the command. That is the only evidence a valve is actually
 *     open. Green.
 *
 *   Collapsing the two is what made a failed command indistinguishable from a
 *   working one, and it is the same mistake the live map makes when it treats
 *   an old bitmask as a present-tense answer.
 *
 * The reading has to be ASKED FOR on an XBee site — nothing reports its outputs
 * unprompted there — which is why confirmation is a second command rather than
 * a longer wait.
 */

/**
 * `sending`      posted, nothing heard back yet
 * `acked`        the site says it carried the command out
 * `confirming`   a reading has been asked for
 * `confirmed`    the node reports the output as commanded
 * `mismatch`     the node reports the OPPOSITE of what was commanded
 * `unconfirmed`  nothing came back in time — not a failure, not a success
 * `failed`       the site refused it, or could not be reached
 */
export type RelayCommandPhase =
  | 'sending'
  | 'acked'
  | 'confirming'
  | 'confirmed'
  | 'mismatch'
  | 'unconfirmed'
  | 'failed';

export interface RelayCommand {
  /** What the tap asked for. */
  want: boolean;
  phase: RelayCommandPhase;
  /** The site's own words, when it had any. */
  detail: string | null;
  /** When the phase last changed — the TTL below runs from here. */
  at: number;
}

/**
 * How long a finished tap keeps colouring its tile before the plain bitmask
 * takes over again.
 *
 * Long enough to walk back from the valve and read it; short enough that a
 * verdict about one tap is not still on screen when it has stopped describing
 * anything. A manual run stops itself after 60 s, and the run is followed to
 * that point, so this only has to outlast the last reading.
 */
export const RELAY_COMMAND_TTL_MS = 90_000;

export type RelayTone = 'off' | 'on' | 'commanded' | 'failed' | 'busy';

export interface RelayTile {
  tone: RelayTone;
  /** The big second line: ON, OFF, or a spinner's worth of nothing. */
  state: string;
  /** What the colour means, in words. Null when the colour is the whole story. */
  note: string | null;
}

/**
 * The tile for one relay.
 *
 * With no tap in play this is the node's bitmask. A mask read from the node's
 * pins is a confirmed state, the same green a tap earns. One the bridge only
 * worked out from an acknowledged command (`source` 'ack', a scheduled step
 * opening or closing a valve) is amber until the read that follows it, two
 * seconds or so later (Rob, 2026-10-19). A mask that does not say where it
 * came from (null) is trusted, as it always was.
 */
export function relayTile(
  cmd: RelayCommand | null | undefined,
  on: boolean,
  nowMs: number,
  source: string | null = null,
): RelayTile {
  const live = cmd && nowMs - cmd.at < RELAY_COMMAND_TTL_MS ? cmd : null;
  if (!live) {
    const state = on ? 'ON' : 'OFF';
    if (source === 'ack') return { tone: 'commanded', state, note: 'acknowledged' };
    return { tone: on ? 'on' : 'off', state, note: null };
  }

  const wanted = live.want ? 'ON' : 'OFF';
  switch (live.phase) {
    case 'sending':
      return { tone: 'busy', state: '…', note: 'sending' };
    case 'acked':
      return { tone: 'commanded', state: wanted, note: 'acknowledged' };
    case 'confirming':
      return { tone: 'commanded', state: wanted, note: 'checking' };
    case 'confirmed':
      return { tone: live.want ? 'on' : 'off', state: wanted, note: 'confirmed' };
    case 'mismatch':
      // The node's answer, not ours — it is the one that can be acted on.
      return { tone: 'failed', state: on ? 'ON' : 'OFF', note: 'did not switch' };
    case 'unconfirmed':
      return { tone: 'commanded', state: wanted, note: 'not confirmed' };
    case 'failed':
      return { tone: 'failed', state: on ? 'ON' : 'OFF', note: 'failed' };
  }
}

/**
 * Fold in what the SCHEDULE is doing, for a tile with no tap of its own.
 *
 * A program opening a valve makes exactly the same two-stage claim a tap does,
 * so it gets the same two colours. The bridge has opened the window and the
 * node has not reported since: amber, our claim, no evidence. The node reports
 * the output shut inside its own window: red, and a dry block. A node
 * reporting it OPEN needs nothing from here — the bitmask is already green, on
 * the same evidence a confirmed tap earns.
 *
 * A live tap outranks all of it. Somebody standing at the valve pressing a
 * control wants to know what THAT did.
 */
export function withScheduledTone(
  tile: RelayTile,
  valveState: string | null | undefined,
): RelayTile {
  if (tile.tone !== 'off' || tile.note) return tile;
  if (valveState === 'due') return { ...tile, tone: 'commanded' };
  if (valveState === 'did-not-start') return { ...tile, tone: 'failed' };
  return tile;
}

/** True while a tap is still in flight and the relay must not be tapped again. */
export function relayCommandBusy(cmd: RelayCommand | null | undefined): boolean {
  return cmd?.phase === 'sending' || cmd?.phase === 'acked' || cmd?.phase === 'confirming';
}

/**
 * What the next tap should ask for.
 *
 * An in-flight or just-finished tap outranks the bitmask: on a site whose
 * outputs are only read when asked, the bitmask can still be reporting the
 * state from before the tap, and tapping "off" would re-send "on".
 */
export function relayIntendedState(
  cmd: RelayCommand | null | undefined,
  on: boolean,
  nowMs: number,
): boolean {
  if (!cmd || nowMs - cmd.at >= RELAY_COMMAND_TTL_MS) return on;
  // A command the node contradicted, or one that never left, says nothing about
  // where the relay is now; the reading does.
  if (cmd.phase === 'mismatch' || cmd.phase === 'failed') return on;
  return cmd.want;
}
